import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Plus, Link2, Copy, ExternalLink, Trash2, Edit2, ToggleLeft, QrCode, Download, AlertCircle, MessageSquare, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { canCreatePaymentLinks, paymentLinkMessage } from '../../lib/accountPermissions';

const JJUMA_DOMAIN = 'pay.jjuma.com';

const PURPOSES = [
  { value: 'donation', label: 'Donation' },
  { value: 'service', label: 'Service' },
  { value: 'product', label: 'Product' },
  { value: 'tip', label: 'Tip' },
  { value: 'event', label: 'Event' },
  { value: 'church', label: 'Church' },
  { value: 'ngo', label: 'NGO' },
  { value: 'creator_support', label: 'Creator Support' },
  { value: 'other', label: 'Other' },
];

function buildLinkForm(link, userCurrency) {
  if (!link) {
    return {
      link_type: 'standard',
      title: '', slug: '', description: '', amount_type: 'open',
      fixed_amount: '', currency: userCurrency || 'UGX', purpose_category: 'other',
      provider: 'auto',
      providers: [],
      thank_you_message: 'Thank you for your payment!', redirect_url: '',
      featured_image_url: '', donation_story: '', suggested_amounts: '5000,10000,20000,50000,100000',
      goal_amount: '', amount_raised: '', donor_count: '', anonymous_allowed: true,
      recurring_placeholder: false, customer_name_required: true, customer_email_required: true, customer_phone_required: false,
    };
  }

  return {
    link_type: link.link_type || (link.purpose_category === 'donation' ? 'donation' : 'standard'),
    title: link.title || '',
    slug: link.slug || '',
    description: link.description || '',
    amount_type: link.amount_type || 'open',
    fixed_amount: link.fixed_amount || '',
    currency: link.currency || userCurrency || 'UGX',
    purpose_category: link.purpose_category || 'other',
    provider: link.provider || 'auto',
    providers: Array.isArray(link.providers) ? link.providers : [],
    thank_you_message: link.thank_you_message || 'Thank you for your payment!',
    redirect_url: link.redirect_url || '',
    featured_image_url: link.featured_image_url || '',
    donation_story: link.donation_story || '',
    suggested_amounts: Array.isArray(link.suggested_amounts) ? link.suggested_amounts.join(',') : (link.suggested_amounts || '5000,10000,20000,50000,100000'),
    goal_amount: link.goal_amount || '',
    amount_raised: link.amount_raised || '',
    donor_count: link.donor_count || '',
    anonymous_allowed: link.anonymous_allowed !== undefined ? !!link.anonymous_allowed : true,
    recurring_placeholder: !!link.recurring_placeholder,
    customer_name_required: link.customer_name_required !== undefined ? !!link.customer_name_required : true,
    customer_email_required: link.customer_email_required !== undefined ? !!link.customer_email_required : true,
    customer_phone_required: !!link.customer_phone_required,
  };
}

function CreateLinkDialog({ open, onOpenChange, onSubmitted, userCurrency, canCreate, permissionMessage, link, mode = 'create' }) {
  const isEditing = mode === 'edit' && !!link;
  const [form, setForm] = useState(() => buildLinkForm(link, userCurrency));
  const [loading, setLoading] = useState(false);
  const [providerState, setProviderState] = useState(null);
  const [providerLoading, setProviderLoading] = useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleProvider = (providerId) => {
    setForm(prev => {
      const current = Array.isArray(prev.providers) ? prev.providers : [];
      const next = current.includes(providerId)
        ? current.filter(p => p !== providerId)
        : [...current, providerId];
      return { ...prev, providers: next };
    });
  };

  useEffect(() => {
    if (!open) return;
    setForm(buildLinkForm(link, userCurrency));
  }, [open, link, userCurrency]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const loadProviders = async () => {
      setProviderLoading(true);
      try {
        const r = await api.get(`/dashboard/payment-providers?currency=${encodeURIComponent(form.currency)}`);
        if (!alive) return;
        const data = r.data || {};
        setProviderState(data);
        const enabledIds = (data.providers || []).map(p => p.id);
        if (enabledIds.length === 1) {
          if (form.provider !== enabledIds[0]) update('provider', enabledIds[0]);
          if (!form.providers?.length) update('providers', enabledIds);
        } else if (!form.providers?.length && enabledIds.length > 1) {
          update('providers', enabledIds);
        } else if (form.provider !== 'auto' && !enabledIds.includes(form.provider)) {
          update('provider', data.default_provider || 'auto');
        }
      } catch (err) {
        if (!alive) return;
        setProviderState({ providers: [], can_create: false, warning: err.response?.data?.detail || 'Unable to load payment gateways.' });
      } finally {
        if (alive) setProviderLoading(false);
      }
    };
    loadProviders();
    return () => { alive = false; };
  }, [open, form.currency, form.provider]);

  const enabledProviders = providerState?.providers || [];
  const providerBlocked = providerState && providerState.can_create === false;

  const handleSlugChange = (val) => {
    const slug = val.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/--+/g, '-');
    update('slug', slug);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) {
      toast.error(permissionMessage || 'Your account must be verified before you can create payment links.');
      return;
    }
    if (providerBlocked) {
      toast.error(providerState?.warning || 'No payment gateway is currently available.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      payload.providers = Array.isArray(form.providers) ? form.providers : [];
      payload.suggested_amounts = form.link_type === 'donation'
        ? String(form.suggested_amounts || '').split(',').map(v => v.trim()).filter(Boolean)
        : [];
      if (form.amount_type === 'open') delete payload.fixed_amount;
      else payload.fixed_amount = parseFloat(form.fixed_amount);
      if (form.link_type !== 'donation') {
        delete payload.featured_image_url;
        delete payload.donation_story;
        delete payload.suggested_amounts;
        delete payload.goal_amount;
        delete payload.amount_raised;
        delete payload.donor_count;
        delete payload.anonymous_allowed;
        delete payload.recurring_placeholder;
      } else {
        payload.goal_amount = form.goal_amount ? parseFloat(form.goal_amount) : null;
        payload.amount_raised = form.amount_raised ? parseFloat(form.amount_raised) : 0;
        payload.donor_count = form.donor_count ? parseInt(form.donor_count, 10) : 0;
      }
      if (isEditing) {
        delete payload.slug;
        await api.put(`/payment-links/${link.id}`, payload);
        toast.success('Payment link updated!');
      } else {
        await api.post('/payment-links', payload);
        toast.success('Payment link created!');
      }
      onSubmitted?.();
      onOpenChange(false);
      setForm(buildLinkForm(null, userCurrency));
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} link`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{isEditing ? 'Edit Payment Link' : 'Create Payment Link'}</DialogTitle>
        </DialogHeader>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-900">Quick tools</p>
            <p className="text-xs text-slate-500 mt-0.5">Templates and bulk links are available from the dashboard.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => { onOpenChange(false); navigate('/dashboard/whatsapp-templates'); }}>
              <MessageSquare size={14} className="mr-1.5" /> Templates
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => { onOpenChange(false); navigate('/dashboard/bulk-payment-links'); }}>
              <Upload size={14} className="mr-1.5" /> Bulk Links
            </Button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Link Type</Label>
            <Select value={form.link_type} onValueChange={v => {
              update('link_type', v);
              update('purpose_category', v === 'donation' ? 'donation' : 'other');
            }}>
              <SelectTrigger className="mt-1" data-testid="link-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard Payment</SelectItem>
                <SelectItem value="donation">Donation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{form.link_type === 'donation' ? 'Donation Title' : 'Title'}</Label>
            <Input data-testid="link-title-input" value={form.title} onChange={e => update('title', e.target.value)} placeholder={form.link_type === 'donation' ? 'e.g. Support My Mission' : 'e.g. Support My Content'} className="mt-1" required />
          </div>
          {isEditing ? (
            <div>
              <Label>Link Slug</Label>
              <Input value={form.slug} className="mt-1 bg-slate-50" disabled />
              <p className="text-[11px] text-slate-500 mt-1">Link slug is managed separately and cannot be changed here.</p>
            </div>
          ) : (
            <div>
              <Label>Link Slug</Label>
              <div className="flex items-center mt-1 gap-2">
                <span className="text-sm text-slate-400">{JJUMA_DOMAIN}/</span>
                <Input data-testid="link-slug-input" value={form.slug} onChange={e => handleSlugChange(e.target.value)} placeholder="your-link" required />
              </div>
            </div>
          )}
          {form.link_type === 'donation' ? (
            <>
              <div>
                <Label>Donation Story</Label>
                <Textarea data-testid="link-donation-story-input" value={form.donation_story} onChange={e => update('donation_story', e.target.value)} placeholder="Tell supporters why this matters" className="mt-1" rows={4} />
              </div>
              <div>
                <Label>Featured Image or Logo URL</Label>
                <Input value={form.featured_image_url} onChange={e => update('featured_image_url', e.target.value)} placeholder="https://..." className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Goal Amount</Label>
                  <Input type="number" value={form.goal_amount} onChange={e => update('goal_amount', e.target.value)} placeholder="1000000" className="mt-1" />
                </div>
                <div>
                  <Label>Amount Raised</Label>
                  <Input type="number" value={form.amount_raised} onChange={e => update('amount_raised', e.target.value)} placeholder="250000" className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Number of Donors</Label>
                  <Input type="number" value={form.donor_count} onChange={e => update('donor_count', e.target.value)} placeholder="15" className="mt-1" />
                </div>
                <div>
                  <Label>Suggested Amounts</Label>
                  <Input value={form.suggested_amounts} onChange={e => update('suggested_amounts', e.target.value)} placeholder="5000,10000,20000,50000,100000" className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!form.recurring_placeholder} onChange={e => update('recurring_placeholder', e.target.checked)} />
                  Recurring donation placeholder
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!form.customer_phone_required} onChange={e => update('customer_phone_required', e.target.checked)} />
                  Phone required
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['anonymous_allowed', 'Anonymous donations'],
                  ['customer_name_required', 'Name required'],
                  ['customer_email_required', 'Email required'],
                ].map(([field, label]) => (
                  <label key={field} className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" checked={!!form[field]} onChange={e => update(field, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>
            </>
          ) : (
            <div>
              <Label>Description</Label>
              <Textarea data-testid="link-description-input" value={form.description} onChange={e => update('description', e.target.value)} placeholder="What is this payment for?" className="mt-1" rows={2} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Amount Type</Label>
              <Select value={form.amount_type} onValueChange={v => update('amount_type', v)}>
                <SelectTrigger className="mt-1" data-testid="link-amount-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open Amount</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => update('currency', v)}>
                <SelectTrigger className="mt-1" data-testid="link-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UGX">UGX</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.amount_type === 'fixed' && (
            <div>
              <Label>Fixed Amount</Label>
              <Input data-testid="link-fixed-amount-input" type="number" value={form.fixed_amount} onChange={e => update('fixed_amount', e.target.value)} placeholder="50000" className="mt-1" required />
            </div>
          )}
          <div>
            <Label>Purpose Category</Label>
            <Select value={form.purpose_category} onValueChange={v => update('purpose_category', v)}>
              <SelectTrigger className="mt-1" data-testid="link-purpose-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PURPOSES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {providerLoading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
              Loading payment gateways...
            </div>
          ) : providerBlocked ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {providerState?.warning || 'No payment gateway is currently available.'}
            </div>
          ) : enabledProviders.length > 1 ? (
            <div>
              <Label>Payment Gateway</Label>
              <Select value={form.provider} onValueChange={v => update('provider', v)}>
                <SelectTrigger className="mt-1" data-testid="link-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">{providerState?.auto_label || 'Auto (Best Available)'}</SelectItem>
                  {enabledProviders.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Disabled gateways are hidden automatically.</p>
            </div>
          ) : enabledProviders.length === 1 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <Label className="text-xs text-slate-500">Payment gateway</Label>
              <p className="text-sm font-medium text-slate-900 mt-1">{enabledProviders[0].label}</p>
              <p className="text-[11px] text-slate-500 mt-1">This is the only enabled gateway, so it is selected automatically.</p>
            </div>
          ) : null}
          {!!enabledProviders.length && (
            <div>
              <Label>Enabled Gateways</Label>
              <p className="text-[11px] text-slate-500 mt-1">Choose which gateways should appear on checkout. Leave the selection as-is to keep the enabled gateways available.</p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {enabledProviders.map((provider) => (
                  <label key={provider.id} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${form.providers?.includes(provider.id) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                    <input type="checkbox" checked={form.providers?.includes(provider.id)} onChange={() => toggleProvider(provider.id)} />
                    <span>{provider.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => update('providers', enabledProviders.map(p => p.id))}>All enabled gateways</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => update('providers', [])}>Clear selection</Button>
              </div>
            </div>
          )}
          <div>
            <Label>Thank You Message</Label>
            <Input data-testid="link-thankyou-input" value={form.thank_you_message} onChange={e => update('thank_you_message', e.target.value)} className="mt-1" />
          </div>
          {!canCreate && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {permissionMessage}
            </div>
          )}
          <Button type="submit" data-testid="create-link-submit-btn" disabled={loading || !canCreate || providerBlocked} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
            {loading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Payment Link')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentLinks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [providerSummary, setProviderSummary] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const canCreate = canCreatePaymentLinks(user);
  const permissionMessage = paymentLinkMessage();

  const fetchLinks = useCallback(() => {
    if (!canCreate) {
      setLoading(false);
      return;
    }
    api.get('/payment-links').then(r => { setLinks(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [canCreate]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  useEffect(() => {
    if (!canCreate) {
      setProviderSummary(null);
      return;
    }
    let alive = true;
    api.get(`/dashboard/payment-providers?currency=${encodeURIComponent(user?.default_currency || 'UGX')}`)
      .then(r => { if (alive) setProviderSummary(r.data || null); })
      .catch(() => { if (alive) setProviderSummary(null); });
    return () => { alive = false; };
  }, [canCreate, user?.default_currency]);

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`https://${JJUMA_DOMAIN}/pay/${slug}`);
    toast.success('Link copied to clipboard!');
  };

  const showQR = async (slug, title) => {
    try {
      const r = await api.get(`/export/qrcode/${slug}`);
      setQrData({ ...r.data, slug, title });
    } catch { toast.error('Failed to generate QR code'); }
  };

  const downloadQR = () => {
    if (!qrData) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrData.qr_base64}`;
    link.download = `jjuma-pay-${qrData.slug}-qr.png`;
    link.click();
  };

  const toggleLink = async (id) => {
    try {
      const r = await api.patch(`/payment-links/${id}/toggle`);
      toast.success(r.data.message);
      fetchLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle link');
    }
  };

  const deleteLink = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await api.delete(`/payment-links/${id}`);
      toast.success('Link deleted');
      fetchLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Links</h1>
          <p className="text-slate-500 text-sm mt-0.5">Create and manage your payment links</p>
        </div>
        <Button
          onClick={() => canCreate ? setShowCreate(true) : navigate('/dashboard/verification')}
          disabled={providerSummary?.can_create === false && canCreate}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid="create-link-btn"
        >
          <Plus size={16} className="mr-1.5" /> Create Link
        </Button>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Quick tools</p>
              <p className="text-xs text-slate-500 mt-0.5">Use WhatsApp templates for messaging and bulk payment links for CSV uploads.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard/whatsapp-templates')}>
                <MessageSquare size={14} className="mr-1.5" /> WhatsApp Templates
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard/bulk-payment-links')}>
                <Upload size={14} className="mr-1.5" /> Bulk Payment Links
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canCreate && providerSummary?.warning && (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
          {providerSummary.warning}
        </div>
      )}

      {!canCreate && (
        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">{permissionMessage}</p>
              <p className="text-xs text-amber-800 mt-0.5">Complete your verification and admin approval to unlock payment links.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/verification')} className="shrink-0">
            Go to Verification
          </Button>
        </div>
      )}

      <CreateLinkDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSubmitted={fetchLinks}
        userCurrency={user?.default_currency}
        canCreate={canCreate}
        permissionMessage={permissionMessage}
        mode="create"
      />

      <CreateLinkDialog
        open={!!editingLink}
        onOpenChange={(open) => { if (!open) setEditingLink(null); }}
        onSubmitted={fetchLinks}
        userCurrency={user?.default_currency}
        canCreate={canCreate}
        permissionMessage={permissionMessage}
        link={editingLink}
        mode="edit"
      />

      {links.length === 0 ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="py-12 text-center">
            <Link2 size={40} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No payment links yet</h3>
            <p className="text-sm text-slate-500 mb-4">Create your first payment link to start receiving payments</p>
            <Button
              onClick={() => canCreate ? setShowCreate(true) : navigate('/dashboard/verification')}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="create-first-link-btn"
            >
              <Plus size={16} className="mr-1.5" /> Create Your First Link
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {links.map(link => (
            <Card key={link.id} className="bg-white border border-slate-200 shadow-sm rounded-2xl hover:shadow-md hover:border-emerald-200 transition-all" data-testid={`payment-link-card-${link.slug}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-slate-900 truncate">{link.title}</h3>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{JJUMA_DOMAIN}/pay/{link.slug}</p>
                  </div>
                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${link.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                    {link.status}
                  </span>
                </div>
                {link.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{link.description}</p>}
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                  <span className="bg-slate-50 px-2 py-0.5 rounded">{link.purpose_category?.replace('_', ' ')}</span>
                  <span className="bg-slate-50 px-2 py-0.5 rounded">{link.amount_type === 'fixed' ? `${link.currency} ${link.fixed_amount?.toLocaleString()}` : 'Open amount'}</span>
                  <span className="bg-slate-50 px-2 py-0.5 rounded">{link.currency}</span>
                  {link.provider && link.provider !== 'auto' && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded capitalize">Gateway: {link.provider}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingLink(link)} data-testid={`edit-link-${link.slug}`}>
                    <Edit2 size={13} className="mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyLink(link.slug)} data-testid={`copy-link-${link.slug}`}>
                    <Copy size={13} className="mr-1" /> Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(`/pay/${link.slug}`, '_blank')} data-testid={`open-link-${link.slug}`}>
                    <ExternalLink size={13} className="mr-1" /> Open
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => showQR(link.slug, link.title)} data-testid={`qr-link-${link.slug}`}>
                    <QrCode size={13} className="mr-1" /> QR
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleLink(link.id)} data-testid={`toggle-link-${link.slug}`}>
                    <ToggleLeft size={13} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteLink(link.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-link-${link.slug}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* QR Code Dialog */}
      <Dialog open={!!qrData} onOpenChange={v => { if (!v) setQrData(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Payment QR Code</DialogTitle>
          </DialogHeader>
          {qrData && (
            <div className="text-center space-y-4">
              <p className="text-sm text-slate-600">{qrData.title}</p>
              <div className="bg-white p-4 rounded-2xl border border-slate-200 inline-block mx-auto">
                <img src={`data:image/png;base64,${qrData.qr_base64}`} alt="QR Code" className="w-48 h-48 mx-auto" data-testid="qr-code-image" />
              </div>
              <p className="text-xs text-slate-400 mono-nums break-all">{JJUMA_DOMAIN}/pay/{qrData.slug}</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={() => copyLink(qrData.slug)} data-testid="qr-copy-link">
                  <Copy size={14} className="mr-1" /> Copy Link
                </Button>
                <Button size="sm" onClick={downloadQR} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="qr-download">
                  <Download size={14} className="mr-1" /> Download QR
                </Button>
              </div>
              <p className="text-[10px] text-slate-400">Share this QR code on social media, print on flyers, or display in your shop</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
